import { For, Show, createEffect, createSignal, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';

import { unequipItem, type DollSlot } from '../net/protocol';
import type { GameStore } from '../state/store';
import { slotLabel, slotsFor, unmetRequirements } from './character';
import { ItemTooltipBody } from './ItemTooltip';
import { useDrag } from './DragContext';
import { Icon } from './Icon';
import { placeTooltip, type Point } from './tooltip';

/**
 * The paper doll, per `doc/grind/design/EQUIPMENT-SLOTS.md`.
 *
 * Laid out as the spec's ASCII diagram reads — a column of armour down the
 * middle, jewellery either side, weapons along the bottom — rather than as a
 * flat list, because the diagram *is* the design.
 */
const DOLL_LAYOUT: { left: DollSlot[]; centre: DollSlot[]; right: DollSlot[] } = {
  left: ['AMULET', 'GLOVES'],
  centre: ['HEAD', 'CHEST', 'BELT', 'LEGS', 'BOOTS'],
  right: ['RING_1', 'RING_2'],
};

const HANDS: DollSlot[] = ['MAIN_HAND', 'OFF_HAND'];

/**
 * The eleven sockets, as a drag source and a drop target.
 *
 * **It lives beside the bag rather than on the character sheet**, which is the split the
 * roadmap asked for. Equipping is a drag from the grid onto a socket, so the two have to
 * be on screen together — and `menu` holds one panel at a time, deliberately, because
 * every panel covers the canvas. Putting the doll on the stats screen and the grid on
 * another key would have made the central gesture of the genre impossible.
 *
 * It is the Diablo arrangement, arrived at from the constraint rather than copied: the
 * *inventory* screen is the doll plus the grid, and the *character* screen is the
 * numbers. `CharacterSheet` is now the numbers.
 */
export const PaperDoll: Component<{ store: GameStore }> = (props) => {
  const [inspecting, setInspecting] = createSignal<DollSlot | null>(null);

  return (
    <section class="sheet__doll" aria-label="Equipment">
      <div class="doll">
        <div class="doll__column">
          <For each={DOLL_LAYOUT.left}>
            {(slot) => (
              <DollSquare
                store={props.store}
                slot={slot}
                onInspect={setInspecting}
                inspecting={inspecting()}
              />
            )}
          </For>
        </div>
        <div class="doll__column doll__column--centre">
          <For each={DOLL_LAYOUT.centre}>
            {(slot) => (
              <DollSquare
                store={props.store}
                slot={slot}
                onInspect={setInspecting}
                inspecting={inspecting()}
              />
            )}
          </For>
        </div>
        <div class="doll__column">
          <For each={DOLL_LAYOUT.right}>
            {(slot) => (
              <DollSquare
                store={props.store}
                slot={slot}
                onInspect={setInspecting}
                inspecting={inspecting()}
              />
            )}
          </For>
        </div>
      </div>

      <div class="doll__hands">
        <For each={HANDS}>
          {(slot) => (
            <DollSquare
              store={props.store}
              slot={slot}
              onInspect={setInspecting}
              inspecting={inspecting()}
            />
          )}
        </For>
      </div>

      <p class="doll__count" data-testid="character-worn-count">
        {equippedSlots(props.store)} of 11 slots filled
      </p>
    </section>
  );
};

/** How many sockets hold something of their own — the virtual link is not one. */
function equippedSlots(store: GameStore): number {
  return DOLL_SLOT_ORDER.reduce((n, slot) => {
    const worn = store.equipped(slot);
    return worn != null && worn.isVirtualLink !== true ? n + 1 : n;
  }, 0);
}

const DOLL_SLOT_ORDER: DollSlot[] = [
  ...DOLL_LAYOUT.left,
  ...DOLL_LAYOUT.centre,
  ...DOLL_LAYOUT.right,
  ...HANDS,
];

/**
 * One socket of the paper doll: a drag source, a drop target, and a click that takes
 * the item off.
 *
 * The click survives alongside the drag rather than being replaced by it. Two reasons,
 * and the second is the load-bearing one: taking something off is a one-step action
 * that needs no destination, and **a drag cannot be performed with a keyboard**. This is
 * a `<button>`, so it is focusable and Enter still works; the drag is the faster way for
 * a mouse, never the only way.
 */
const DollSquare: Component<{
  store: GameStore;
  slot: DollSlot;
  inspecting: DollSlot | null;
  onInspect: (slot: DollSlot | null) => void;
}> = (props) => {
  const dragging = useDrag();
  const worn = () => props.store.equipped(props.slot);
  const total = () => props.store.character()?.attributes.total;

  // The tooltip is rendered in a portal, fixed to the viewport, because the
  // panel around it scrolls: `overflow-y: auto` forces `overflow-x` to `auto`
  // too, so a tooltip centred on a slot in the leftmost column was clipped in
  // half by the panel edge. Positioning is measured rather than guessed —
  // placeTooltip needs the tooltip's real height to know whether it fits below.
  let anchorEl: HTMLDivElement | undefined;
  let tipEl: HTMLDivElement | undefined;
  const [tipAt, setTipAt] = createSignal<Point | null>(null);

  createEffect(() => {
    // Re-runs when the hovered slot changes, and when the item in it does.
    const showing = props.inspecting === props.slot && worn() != null;
    if (!showing || !anchorEl || !tipEl) {
      setTipAt(null);
      return;
    }

    const a = anchorEl.getBoundingClientRect();
    const t = tipEl.getBoundingClientRect();
    setTipAt(
      placeTooltip(
        { left: a.left, top: a.top, width: a.width, height: a.height },
        { width: t.width, height: t.height },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  });

  /**
   * A worn item whose gate is no longer met — take off the amulet granting the
   * strength and the greatsword it enabled is flagged rather than silently
   * unequipped. The spec's rule for skills, applied to gear.
   */
  const unmet = () => {
    const t = total();
    const w = worn();
    if (!t || !w || w.isVirtualLink) return [];
    return unmetRequirements(t, w.requires);
  };

  /**
   * Whether the drag in flight could land here.
   *
   * A preview, exactly like the grid's cell highlight: the server re-checks the slot,
   * the attribute gate and the two-handed rules. What it can answer locally is the two
   * things that are purely about *where* — a vaulted item cannot be worn directly, and
   * a socket cannot receive itself.
   */
  const candidate = () => {
    const d = dragging.drag();
    if (!d) return false;
    if (d.origin.kind === 'doll') return d.origin.slot !== props.slot;
    return d.origin.container === 'BAG';
  };

  /**
   * Whether the item being *read* somewhere else in the HUD could be worn here.
   *
   * Hovering a sword in the bag lights both hands; hovering a ring lights both rings.
   * Asked for directly — *the hovering should also put an overlay over the slots of
   * equipment it fits* — and it answers a question the tooltip cannot: a tooltip says
   * `RING`, and the doll has two of them.
   *
   * Deliberately **not** suppressed while a drag is in flight: `candidate` marks where a
   * drop may land and this marks where the item belongs, and during a drag of that same
   * item both are true and say the same thing. The item's own socket is excluded, because
   * lighting the place it already is says nothing.
   */
  const fits = () => {
    const item = dragging.inspecting();
    if (!item) return false;
    if (worn()?.item.uuid === item.uuid) return false;
    return slotsFor(item).includes(props.slot);
  };

  return (
    <div class="doll__slot-wrap" ref={anchorEl}>
      <button
        type="button"
        class="doll__slot"
        classList={{
          'doll__slot--filled': worn() != null && !worn()?.isVirtualLink,
          'doll__slot--linked': worn()?.isVirtualLink === true,
          'doll__slot--unmet': unmet().length > 0,
          'doll__slot--candidate': candidate(),
          'doll__slot--fits': fits(),
          [`doll__slot--${worn()?.item.rarity.toLowerCase() ?? 'empty'}`]: worn() != null,
        }}
        data-testid={`slot-${props.slot}`}
        aria-label={slotLabel(props.slot)}
        onMouseEnter={() => props.onInspect(props.slot)}
        onMouseLeave={() => props.onInspect(null)}
        onFocus={() => props.onInspect(props.slot)}
        onBlur={() => props.onInspect(null)}
        onPointerDown={(event) => {
          // Primary button only — a right-click must reach `onContextMenu` rather than
          // beginning a drag it would then cancel. See the note in `ItemGrid`.
          if (event.button !== 0) return;

          const w = worn();
          // The off-hand of a two-hander holds nothing of its own, so there is nothing
          // to pick up from it — the weapon is in the main hand.
          if (!w || w.isVirtualLink) return;
          dragging.begin(
            w.item,
            { kind: 'doll', slot: props.slot },
            { col: 0, row: 0 },
            { x: event.clientX, y: event.clientY },
          );
        }}
        onPointerEnter={() => dragging.hover(null)}
        onPointerUp={() => {
          if (!dragging.drag()) return;
          dragging.drop({ kind: 'doll', slot: props.slot });
        }}
        /**
         * Right-click takes it off, mirroring the bag's right-click-to-equip: the two
         * halves of the same gesture, so a player can dress and undress without ever
         * dragging. `preventDefault` stops the browser menu opening over the doll.
         *
         * During a drag it cancels instead, the same rule the grid's cells follow.
         */
        onContextMenu={(event) => {
          event.preventDefault();
          if (dragging.drag()) {
            dragging.cancel();
            return;
          }
          const w = worn();
          if (!w) return;
          props.store.send(unequipItem(w.isVirtualLink ? 'MAIN_HAND' : props.slot));
        }}
        onClick={() => {
          // A click still takes it off, and it has to: a drag needs a mouse, and this
          // is a focusable button. The off-hand of a two-hander is not a real occupant,
          // so clicking it takes the weapon off the main hand, which is what is there.
          //
          // Skipped while a drag is resolving: `pointerup` fires first and a click
          // afterwards would unequip whatever had just been placed.
          if (dragging.drag()) return;
          const w = worn();
          if (!w) return;
          props.store.send(unequipItem(w.isVirtualLink ? 'MAIN_HAND' : props.slot));
        }}
      >
        <Show when={worn()} fallback={<span class="doll__empty">·</span>}>
          {(w) => <Icon class="doll__glyph" iconId={w().item.iconId} glyph={w().item.glyph} />}
        </Show>
      </button>
      <span class="doll__label">{slotLabel(props.slot)}</span>

      <Show when={props.inspecting === props.slot && worn()}>
        {(w) => (
          <Portal>
            <div
              ref={tipEl}
              class="doll__tip"
              data-testid={`slot-tip-${props.slot}`}
              role="tooltip"
              style={{
                left: `${tipAt()?.left ?? 0}px`,
                top: `${tipAt()?.top ?? 0}px`,
                // Hidden until measured, so it never flashes in the wrong place
                // on the frame before its size is known.
                visibility: tipAt() ? 'visible' : 'hidden',
              }}
            >
              {/*
                **One tooltip, not two.** This used to be its own copy of the whole card — name,
                meta row, requirement line, affix list — and it had drifted from the bag's: the DPS
                headline, the toughness comparison and the shortfall wording all landed on
                `ItemTooltipBody` and none of them reached a worn item. Reported directly: *the
                tooltip of an equipped item and an unequipped one should be the same tooltip.*

                Exactly the fault `affixLine` and `Icon.tsx` were both written to end. A card
                rendered in two places is a card that is right in one of them.

                **`worn` is deliberately not passed**, and that is the only difference the spec
                allows: an equipped item has nothing to compare against but itself, and a block of
                `0 → 0` lines is noise. Everything else — the headline, the gate, the affixes — is
                the same component.
              */}
              <ItemTooltipBody
                item={w().item}
                attributes={total() ?? null}
                note={w().isVirtualLink ? 'Both hands — this slot is held by your weapon.' : undefined}
                toughness={props.store.character()?.derived.toughness}
              />
            </div>
          </Portal>
        )}
      </Show>
    </div>
  );
};
